import I18n from 'I18n';
import { SAME_TIME, HALF_HOUR_BEFORE, HOUR_BEFORE, DAY_BEFORE, WEEK_BEFORE, TODAY, TOMORROW, ONE_BUSINESS_DAY, TWO_BUSINESS_DAYS, THREE_BUSINESS_DAYS, ONE_WEEK, TWO_WEEKS, ONE_MONTH, THREE_MONTHS, SIX_MONTHS, CUSTOM } from '../constants/RelativeDates';
import formatShortDate from 'I18n/utils/formatShortDate';
import unescapedText from 'I18n/utils/unescapedText';
const FRIDAY = 5;
const SATURDAY = 6;
const SUNDAY = 7;
const NEXT_MONDAY = 8;
export function snapToBusinessDay(momentDate) {
  if (momentDate.isoWeekday() === SATURDAY || momentDate.isoWeekday() === SUNDAY) {
    // set to the monday after
    return momentDate.clone().isoWeekday(NEXT_MONDAY);
  }
  return momentDate;
}
export function addBusinessDays(fromDate, days) {
  let toDate = fromDate.clone();
  for (let i = 0; i < days; i++) {
    toDate = snapToBusinessDay(toDate.add(1, 'day'));
  }
  return toDate;
}
export function datePresetToMoment(presetKey, relatesTo = I18n.moment.userTz()) {
  let diff;
  switch (presetKey) {
    case SAME_TIME:
      return relatesTo.clone();
    case HALF_HOUR_BEFORE:
      return relatesTo.clone().subtract(0.5, 'hours');
    case HOUR_BEFORE:
      return relatesTo.clone().subtract(1, 'hour');
    case DAY_BEFORE:
      return relatesTo.clone().subtract(1, 'day');
    case WEEK_BEFORE:
      return relatesTo.clone().subtract(1, 'week');
    case TODAY:
      return relatesTo.clone().startOf('day');
    case TOMORROW:
      return relatesTo.clone().add(1, 'day').startOf('day');
    case ONE_BUSINESS_DAY:
      return addBusinessDays(relatesTo, 1).startOf('day');
    case TWO_BUSINESS_DAYS:
      return addBusinessDays(relatesTo, 2).startOf('day');
    case THREE_BUSINESS_DAYS:
      return addBusinessDays(relatesTo, 3).startOf('day');
    case ONE_WEEK:
      diff = [1, 'week'];
      break;
    case TWO_WEEKS:
      diff = [2, 'weeks'];
      break;
    case ONE_MONTH:
      diff = [1, 'month'];
      break;
    case THREE_MONTHS:
      diff = [3, 'months'];
      break;
    case SIX_MONTHS:
      diff = [6, 'months'];
      break;
    default:
      return relatesTo.clone();
  }
  return snapToBusinessDay(relatesTo.clone().add(...diff).startOf('day'));
}
export function momentToDatePreset(value, presets, relatesTo = I18n.moment.userTz()) {
  return presets.find(presetKey => {
    const presetValue = datePresetToMoment(presetKey, relatesTo);
    return I18n.moment(value).isSame(presetValue, 'day');
  });
}
export function getRelativeLabel(presetKey, relatesTo = I18n.moment.userTz()) {
  const formattedDay = datePresetToMoment(presetKey, relatesTo).format('dddd');
  const formattedDate = formatShortDate(datePresetToMoment(presetKey, relatesTo), 'userTz');
  switch (presetKey) {
    case TODAY:
      return I18n.text('customerDataProperties.PropertyInputDateTime.preset.today');
    case TOMORROW:
      return I18n.text('customerDataProperties.PropertyInputDateTime.preset.tomorrow');
    case ONE_BUSINESS_DAY:
      return I18n.text('customerDataProperties.PropertyInputDateTime.preset.oneBusinessDay', {
        day: formattedDay
      });
    case TWO_BUSINESS_DAYS:
      return I18n.text('customerDataProperties.PropertyInputDateTime.preset.twoBusinessDays', {
        day: formattedDay
      });
    case THREE_BUSINESS_DAYS:
      return I18n.text('customerDataProperties.PropertyInputDateTime.preset.threeBusinessDays', {
        day: formattedDay
      });
    case ONE_WEEK:
      return I18n.text('customerDataProperties.PropertyInputDateTime.preset.oneWeek', {
        date: formattedDate
      });
    case TWO_WEEKS:
      return I18n.text('customerDataProperties.PropertyInputDateTime.preset.twoWeeks', {
        date: formattedDate
      });
    case ONE_MONTH:
      return I18n.text('customerDataProperties.PropertyInputDateTime.preset.oneMonth', {
        date: formattedDate
      });
    case THREE_MONTHS:
      return I18n.text('customerDataProperties.PropertyInputDateTime.preset.threeMonths', {
        date: formattedDate
      });
    case SIX_MONTHS:
      return I18n.text('customerDataProperties.PropertyInputDateTime.preset.sixMonths', {
        date: formattedDate
      });
    default:
      return relatesTo.clone().format('MM/DD/YYYY');
  }
}
const dueDatePresetConfig = [TODAY, TOMORROW, ONE_BUSINESS_DAY, TWO_BUSINESS_DAYS, THREE_BUSINESS_DAYS, ONE_WEEK, TWO_WEEKS, ONE_MONTH, THREE_MONTHS, SIX_MONTHS, CUSTOM];
export function getDueDatePresets(relatesTo = I18n.moment.userTz()) {
  // The whole list of options includes 'Tomorrow' and
  // '1 business day', which in most cases are the same, so we usually delete
  // the '1 business day' option. On fridays and saturdays, they're different
  // (1bd -> monday, tomorrow -> sat or sun). So we leave both in.
  if (![FRIDAY, SATURDAY].includes(relatesTo.day())) {
    return dueDatePresetConfig.filter(cat => cat !== ONE_BUSINESS_DAY);
  }
  return dueDatePresetConfig;
}
export function getTaskReminderPresets() {
  return [SAME_TIME, HALF_HOUR_BEFORE, HOUR_BEFORE, DAY_BEFORE, WEEK_BEFORE];
}
export function getFormattedOptions(options, relatesTo = I18n.moment.userTz()) {
  return options.map(cat => {
    if (cat === CUSTOM) {
      return {
        value: cat,
        text: unescapedText('customerDataProperties.PropertyInputDateTime.custom'),
        key: cat
      };
    }
    return {
      value: datePresetToMoment(cat, relatesTo).valueOf(),
      text: getRelativeLabel(cat, relatesTo),
      key: cat
    };
  });
}